import type { CSSProperties, ReactNode } from "react";
import type { DataElScope } from "@/utils/dataEl";
import { CreferenceInlineReference } from "./CreferenceInlineReference";
import { CreferenceInteractionOverlays } from "./CreferenceInteractionOverlays";
import { CreferenceLeading } from "./CreferenceLeading";
import { CreferenceMode } from "./CreferenceMode";
import { CreferencePillReference } from "./CreferencePillReference";
import { getCreferenceLeadingProps } from "./creferenceLeadingProps";
import { getCreferenceLinkedLabelTextStyle } from "./creferenceLabelTextStyle";
import type { CreferenceInteraction } from "./creferenceInteractionTypes";
import type { CreferenceResolvedEntity } from "./useCreferenceResolvedEntity";

export interface CreferenceContentProps {
  dataElScope: DataElScope;
  className?: string;
  disabled: boolean;
  interaction: CreferenceInteraction;
  mode: CreferenceMode;
  resolvedEntity: CreferenceResolvedEntity;
  style?: CSSProperties;
}

export function CreferenceContent({
  dataElScope,
  className,
  disabled,
  interaction,
  mode,
  resolvedEntity,
  style,
}: CreferenceContentProps): ReactNode {
  const linkedLabelTextStyle = getCreferenceLinkedLabelTextStyle();
  const leading = (
    <CreferenceLeading dataElScope={dataElScope} {...getCreferenceLeadingProps(resolvedEntity)} />
  );
  const content =
    mode === CreferenceMode.Inline ? (
      <CreferenceInlineReference
        dataElScope={dataElScope}
        className={className}
        disabled={disabled}
        interaction={interaction}
        leading={leading}
        linkedLabelTextStyle={linkedLabelTextStyle}
        resolvedEntity={resolvedEntity}
        style={style}
      />
    ) : (
      <CreferencePillReference
        dataElScope={dataElScope}
        className={className}
        disabled={disabled}
        interaction={interaction}
        leading={leading}
        linkedLabelTextStyle={linkedLabelTextStyle}
        resolvedEntity={resolvedEntity}
        style={style}
      />
    );

  return (
    <>
      {content}
      <CreferenceInteractionOverlays
        dataElScope={dataElScope}
        hintAnchorElement={interaction.hintAnchorElement}
        isAlreadyFocusedHintVisible={interaction.isAlreadyFocusedHintVisible}
        isMemberGlimpseVisible={interaction.isMemberGlimpseVisible}
        memberId={resolvedEntity.member?.id ?? null}
        onAlreadyFocusedHintDismiss={interaction.dismissAlreadyFocusedHint}
        onMemberGlimpseClose={interaction.handleMemberGlimpseClose}
      />
    </>
  );
}
